import { SelectDropDown, TabBar, TabItem } from '@wot/components';
import { ITransaction } from '@wot/typings';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  endOfYear,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths
} from 'date-fns';
import { format } from 'date-fns/esm';
import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTab } from '../../hooks/useTab';
import xhr from '../../utils/fetch';
import { currencyFormatter } from '../../utils/functions';
import TableContainer from './components/Table';
import EarningsTable from './EarningsTable';

export default function Earnings() {
  const [chartData, setChartData] = useState<{ label: string; Earnings: number; Expenses: number }[]>([]);
  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const { activeTab, onTabChange } = useTab('Earnings');
  const [range, setRange] = useState('This Week');
  const [lastMonthEarnings, setLastMonthEarnings] = useState(0);
  const [maxEarnings, setmaxEarnings] = useState<{ month: string; total: number }>();

  const [totalPlans, setTotalPlans] = useState(0);
  const [tutorsPayment, setTutorsPayment] = useState(0);
  const [actualEarnings, setActualEarnings] = useState(0);
  const [lastMonthActualEarnings, setLastMonthActualEarnings] = useState(0);
  const [currentMonth] = useState(new Date());
  const [refundsAmount, setRefundsAmount] = useState(0);

  const [profit, setProfit] = useState(0);

  const getData = async (start: string, end: string) => {
    return await xhr({
      url: `/a/stats/earnings?startAfter=${start}&endBefore=${end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          return null;
        }
      })
      .catch((err) => err);
  };

  const getTotalPlans = async (start: string, end: string) => {
    xhr({
      url: `/a/stats/total-plans`,
      method: 'GET',
      params: {
        startAfter: start,
        endBefore: end
      }
    }).then((res) => {
      if (res.status === 'OK') {
        setTotalPlans(res.data.count || 0);
      }
    });
  };

  function daysInMonth(date: Date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }

  useEffect(() => {
    if (range === 'This Week') {
      let start = startOfWeek(new Date()).toString();
      let end = endOfWeek(new Date()).toString();
      getTotalPlans(start, end);
      getData(start, end).then((res) => {
        let temp: any = [];
        let weekDay = startOfWeek(new Date());
        [...new Array(7)].map((_, i) => {
          let day = format(addDays(weekDay, i), 'EEE');
          let date = format(addDays(weekDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'This Month') {
      let start = startOfMonth(new Date()).toString();
      let end = endOfMonth(new Date()).toString();
      getTotalPlans(start, end);
      getData(start, end).then((res) => {
        let temp: any = [];
        let monthDay = startOfMonth(new Date());
        [...new Array(daysInMonth(new Date()))].map((_, i) => {
          let day = format(addDays(monthDay, i), 'LLL d');
          let date = format(addDays(monthDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'Last Month') {
      let month = subMonths(new Date(), 1);
      let start = startOfMonth(month).toString();
      let end = endOfMonth(month).toString();
      getTotalPlans(start, end);
      getData(start, end).then((res) => {
        let temp: any = [];
        let monthDay = startOfMonth(month);
        [...new Array(daysInMonth(month))].map((_, i) => {
          let day = format(addDays(monthDay, i), 'LLL d');
          let date = format(addDays(monthDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'This Year') {
      let start = startOfYear(new Date()).toString();
      let end = endOfYear(new Date()).toString();
      getTotalPlans(start, end);
      getData(start, end).then((res) => {
        let temp: any = [];
        let yearDay = startOfYear(new Date());
        [...new Array(12)].map((_, i) => {
          let day = format(addMonths(yearDay, i), 'LLL');
          const v = res.reduce((acc: any, currValue: any) => {
            if (format(parseISO(currValue.date), 'LLL') === day) {
              return acc + currValue.total;
            } else {
              return acc;
            }
          }, 0);
          temp.push({
            label: day,
            Earnings: v || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
  }, [range]);


  //Last  Month Earnings
  useEffect(() => {
    let month = subMonths(new Date(), 1);
    xhr({
      url: `/a/stats/earnings`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(month).toString(),
        endBefore: endOfMonth(month).toString()
      }
    }).then((res) => {
      if (res.status === 'OK') {
        setLastMonthEarnings(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
  }, []);
  // Max Earnings
  useEffect(() => {
    xhr({
      url: `/a/stats/max-earnings`,
      method: 'GET'
    }).then((res) => {
      if (res.status === 'OK') {
        setmaxEarnings({ month: res.data._id, total: res.data.total });
      }
    });
  }, []);
  // Tutor Payments
  useEffect(() => {
    xhr({
      url: `/a/stats/tutor-payments-total`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(currentMonth).toString(),
        endBefore: endOfMonth(currentMonth).toString()
      }
    }).then((res) => {
      if (res.status === 'OK') {
        if (res.data) {
          setTutorsPayment(res.data.total);
        } else {
          setTutorsPayment(0);
        }
      }
    });
  }, []);
  // Actual Earnings
  useEffect(() => {
    xhr({
      url: `/a/stats/earnings`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(currentMonth).toString(),
        endBefore: endOfMonth(currentMonth).toString()
      }
    }).then((res) => {
      if (res.status === 'OK') {
        setActualEarnings(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0) - tutorsPayment);
      }
    });
    xhr({
      url: `/a/stats/earnings`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(subMonths(currentMonth, 1)).toString(),
        endBefore: endOfMonth(subMonths(currentMonth, 1)).toString()
      }
    }).then((res) => {
      if (res.status === 'OK') {
        if (res.data) {
          setLastMonthActualEarnings(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0) - tutorsPayment);
        }
      }
    });
  }, [tutorsPayment]);

  useEffect(() => {
    xhr({
      url: `/a/refunds`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(currentMonth).toString(),
        endBefore: endOfMonth(currentMonth).toString()
      }
    }).then((res) => {
      if (res.status == 'OK') {
        setRefundsAmount(res.data.total);
      }
    });
  }, []);
  useEffect(() => {
    if (lastMonthActualEarnings > 0) {
      setProfit(
        Number(((Math.abs(lastMonthActualEarnings - actualEarnings) / lastMonthActualEarnings) * 100).toFixed(2))
      );
    } else {
      setProfit(Number((Math.abs(lastMonthActualEarnings - actualEarnings) * 100).toFixed(2)));
    }
  }, [actualEarnings, lastMonthActualEarnings]);

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow white">
      <div className="w-full flex justify-between">
        <h1 className="text-xl font-semibold">Earnings</h1>
      </div>
      <div className="w-full grid grid-cols-4 grid-rows-2 gap-4">
        <div className="w-full flex flex-col p-8 gap-5 row-span-2 col-span-2 bg-neutralBlue2 rounded-md border">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h1 className="text-base font-normal">Total Revenue</h1>
              <div className="w-full max-w-[164px]">
                <SelectDropDown
                  placeholder="Select Range"
                  value={range}
                  onChange={(e) => setRange(e)}
                  options={['This Week', 'This Month', 'Last Month', 'This Year']}
                />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-primary">
              INR {currencyFormatter(Number(chartData.reduce((acc, currValue) => acc + currValue.Earnings, 0).toFixed(2)))}
            </h1>
          </div>

          <div className="w-full h-[200px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={730}
                height={164}
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}
              >
                <CartesianGrid vertical={false} strokeDasharray="1" />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis axisLine={false} width={20} fontSize={12} orientation="right" />
                <Tooltip />
                <Bar dataKey="Earnings" fill="#8884d8" barSize={10}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill="#EA4459" strokeLinecap="round" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="text-sm font-medium">Total Plans Booked</h2>
            <h1 className="text-2xl font-bold">{totalPlans}</h1>
          </div>

          <div className="w-full flex justify-between gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="text-sm font-medium">Last earning amt. </h2>
              <h1 className="text-2xl font-bold">INR {currencyFormatter(lastMonthEarnings)}</h1>
            </div>

            <div className="flex flex-col gap-2">
              <h2 className="text-sm font-medium">Maximum earning</h2>
              <h1 className="text-2xl font-bold">INR {currencyFormatter(maxEarnings?.total || 0)}</h1>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col gap-4 p-4 rounded-md border">
          <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
          <h3 className="text-2xl font-medium">Payments to be made to tutor</h3>
          <h1 className="text-2xl font-bold">INR {currencyFormatter(tutorsPayment)}</h1>
          {/* <p className="text-sm text-red-500">Expenses</p> */}
        </div>
        <div className="w-full flex flex-col gap-4 p-4 rounded-md border">
          <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
          <h3 className="text-2xl font-medium">Refunds</h3>
          <h1 className="text-2xl font-bold">INR {currencyFormatter(refundsAmount)}</h1>
          {/* <p className="text-sm text-red-500">Cancelled Plans</p> */}
        </div>
        <div className="w-full flex flex-col gap-4 p-4 rounded-md border">
          <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
          <h3 className="text-2xl font-medium">Actual Earnings</h3>
          <h1 className="text-2xl font-bold">INR {currencyFormatter(actualEarnings)}</h1>
          {/* <p className="text-sm text-green-500">Expenses</p> */}
        </div>
        <div className="w-full flex flex-col gap-4 p-4 rounded-md border">
          <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
          <h3 className="text-2xl font-medium">Profit Percentage</h3>
          <h1
            className={`text-2xl font-bold ${lastMonthActualEarnings > actualEarnings ? 'text-red-500' : 'text-green-500'
              }`}
          >
            {lastMonthActualEarnings > actualEarnings ? '-' : '+'}
            {profit}%
          </h1>
          <p className="text-sm ">(as compared to previous month)</p>
        </div>
      </div>

      <EarningsTable />

    </div>
  );
}
